import React, { useState, useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useStore } from '../context/StoreContext';
import { format } from 'date-fns';

const { FiX, FiSend, FiUser, FiMail, FiFileText, FiChevronDown, FiCheck, FiPlus } = FiIcons;

const EmailComposer = ({ isOpen, onClose, prefilledTo, prefilledSubject, prefilledBody, prefilledTenantId, prefilledPropertyId }) => {
  const { data, addRecord } = useStore();
  const { tenants, properties, leases, communicationTemplates, documentTemplates, customFieldDefs } = data;

  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [selectedPropertyId, setSelectedPropertyId] = useState('');
  const [showTemplates, setShowTemplates] = useState(false);
  const [showCc, setShowCc] = useState(false);

  // Initialize from props
  useEffect(() => {
    if (isOpen) {
      setTo(prefilledTo || '');
      setSubject(prefilledSubject || '');
      setBody(prefilledBody || '');
      setSelectedTenantId(prefilledTenantId || '');
      setSelectedPropertyId(prefilledPropertyId || '');
      setCc('');
      setShowTemplates(false);
      setShowCc(false);

      // Auto-fill email from tenant
      if (prefilledTenantId) {
        const t = tenants.find(t => t.id === prefilledTenantId);
        if (t?.email && !prefilledTo) setTo(t.email);
      }
    }
  }, [isOpen, prefilledTo, prefilledSubject, prefilledBody, prefilledTenantId, prefilledPropertyId]);

  if (!isOpen) return null;

  const selectedTenant = tenants.find(t => t.id === selectedTenantId);
  const selectedProperty = properties.find(p => p.id === selectedPropertyId);

  // Build merge field replacements
  const buildReplacements = () => {
    const tenant = selectedTenant || {};
    const prop = selectedProperty || {};
    const lease = leases.find(l => l.id === prop.currentLeaseId) || leases.find(l => l.propertyId === prop.id) || {};

    const r = {
      '[Tenant Name]': tenant.name || '', '[Tenant Email]': tenant.email || '',
      '[Tenant Phone]': tenant.phone || '', '[Tenant Employer]': tenant.employer || '',
      '[Tenant Position]': tenant.position || '', '[Emergency Contact]': tenant.emergencyContact || '',
      '[Property Name]': prop.name || '', '[Property Address]': prop.address || '',
      '[Lock Code]': prop.lockCode || '', '[Rent Rate]': prop.rentRate ? '$' + Number(prop.rentRate).toLocaleString() : '',
      '[Lease Start Date]': lease.startDate ? format(new Date(lease.startDate), 'MMMM dd, yyyy') : '',
      '[Lease End Date]': lease.endDate ? format(new Date(lease.endDate), 'MMMM dd, yyyy') : '',
      '[Rent Amount]': (lease.rentAmount || prop.rentRate) ? '$' + Number(lease.rentAmount || prop.rentRate).toLocaleString() : '',
      '[Deposit Amount]': lease.depositAmount ? '$' + Number(lease.depositAmount).toLocaleString() : '',
      '[Current Date]': format(new Date(), 'MMMM dd, yyyy'),
      '[Company Name]': 'PropManagePro',
    };

    (customFieldDefs || []).forEach(cf => {
      const key = `[${cf.name}]`;
      if (tenant.customFields?.[cf.name]) r[key] = tenant.customFields[cf.name];
      else if (prop.customFields?.[cf.name]) r[key] = prop.customFields[cf.name];
    });

    return r;
  };

  const applyReplacements = (text) => {
    if (!text) return '';
    let result = text;
    Object.entries(buildReplacements()).forEach(([key, value]) => {
      if (value) result = result.replace(new RegExp(key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), String(value));
    });
    return result;
  };

  const handleSelectTenant = (tenantId) => {
    setSelectedTenantId(tenantId);
    const t = tenants.find(t => t.id === tenantId);
    if (t?.email) setTo(t.email);

    // Auto-find property for this tenant
    if (!selectedPropertyId) {
      const prop = properties.find(p => p.tenantId === tenantId);
      if (prop) setSelectedPropertyId(prop.id);
    }
  };

  const handleApplyTemplate = (template) => {
    const filledSubject = applyReplacements(template.subject || template.title || '');
    const filledBody = applyReplacements(template.content || '');
    setSubject(filledSubject);
    setBody(filledBody);
    setShowTemplates(false);
  };

  const handleSend = () => {
    if (!to) return;

    // Build mailto URL
    const params = new URLSearchParams();
    if (subject) params.set('subject', subject);
    if (body) params.set('body', body);
    if (cc) params.set('cc', cc);

    const mailtoUrl = `mailto:${encodeURIComponent(to)}?${params.toString()}`;
    window.open(mailtoUrl, '_blank');

    // Log the communication
    addRecord('communications', {
      tenantId: selectedTenantId,
      tenantName: selectedTenant?.name || to,
      propertyId: selectedPropertyId,
      type: 'Email',
      date: format(new Date(), 'yyyy-MM-dd'),
      subject: subject || '(No subject)',
      notes: body.substring(0, 200) + (body.length > 200 ? '...' : ''),
      status: 'Sent',
    });

    onClose();
  };

  const allTemplates = [...(communicationTemplates || []), ...(documentTemplates || []).filter(t => t.docType === 'email')];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[85] p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-indigo-100 text-indigo-600 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiMail} size={16} />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">Compose Email</h2>
              <p className="text-[10px] text-gray-400">Opens in your default email client</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
            <SafeIcon icon={FiX} size={18} />
          </button>
        </div>

        {/* Toolbar */}
        <div className="px-5 py-2 border-b border-gray-50 flex items-center gap-2 shrink-0 bg-gray-50/50">
          <div className="flex items-center gap-2 flex-1">
            <select value={selectedTenantId} onChange={e => handleSelectTenant(e.target.value)}
              className="px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs font-medium text-gray-600 outline-none focus:border-indigo-500 bg-white">
              <option value="">Select tenant...</option>
              {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <select value={selectedPropertyId} onChange={e => setSelectedPropertyId(e.target.value)}
              className="px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs font-medium text-gray-600 outline-none focus:border-indigo-500 bg-white">
              <option value="">Select property...</option>
              {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div className="relative">
            <button onClick={() => setShowTemplates(!showTemplates)}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${showTemplates ? 'bg-indigo-100 text-indigo-700' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
              <SafeIcon icon={FiFileText} size={12} /> Templates <SafeIcon icon={FiChevronDown} size={10} />
            </button>
            {showTemplates && (
              <div className="absolute right-0 top-full mt-1 w-72 bg-white rounded-xl shadow-lg border border-gray-100 z-10 max-h-64 overflow-y-auto">
                {allTemplates.length === 0 ? (
                  <p className="p-4 text-xs text-gray-400 text-center">No email templates yet</p>
                ) : allTemplates.map(t => (
                  <button key={t.id} onClick={() => handleApplyTemplate(t)}
                    className="w-full text-left px-4 py-2.5 hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0">
                    <p className="text-xs font-semibold text-gray-800">{t.title || t.name}</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">{t.category} · {(t.content || '').substring(0, 60)}...</p>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Email Form */}
        <div className="flex-1 overflow-y-auto">
          <div className="border-b border-gray-100">
            <div className="flex items-center px-5 py-2">
              <label className="text-xs font-semibold text-gray-400 w-12 shrink-0">To</label>
              <input value={to} onChange={e => setTo(e.target.value)}
                className="flex-1 py-1.5 text-sm outline-none" placeholder="recipient@email.com" />
              {!showCc && (
                <button onClick={() => setShowCc(true)} className="text-[10px] text-indigo-600 font-semibold hover:text-indigo-700">Cc</button>
              )}
            </div>
            {showCc && (
              <div className="flex items-center px-5 py-2 border-t border-gray-50">
                <label className="text-xs font-semibold text-gray-400 w-12 shrink-0">Cc</label>
                <input value={cc} onChange={e => setCc(e.target.value)}
                  className="flex-1 py-1.5 text-sm outline-none" placeholder="cc@email.com" />
              </div>
            )}
          </div>
          <div className="flex items-center px-5 py-2 border-b border-gray-100">
            <label className="text-xs font-semibold text-gray-400 w-12 shrink-0">Subject</label>
            <input value={subject} onChange={e => setSubject(e.target.value)}
              className="flex-1 py-1.5 text-sm outline-none" placeholder="Email subject..." />
          </div>
          <div className="p-5">
            <textarea value={body} onChange={e => setBody(e.target.value)}
              rows={12}
              className="w-full text-sm leading-relaxed outline-none resize-none"
              placeholder="Write your message here...&#10;&#10;Tip: Select a tenant and property above, then choose a template to auto-fill with their data." />
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2 text-[10px] text-gray-400">
            {selectedTenant && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full font-semibold">
                <SafeIcon icon={FiUser} size={9} /> {selectedTenant.name}
              </span>
            )}
            {selectedProperty && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-600 rounded-full font-semibold">
                {selectedProperty.name}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors">
              Discard
            </button>
            <button onClick={handleSend} disabled={!to}
              className="flex items-center gap-1.5 px-5 py-2 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed">
              <SafeIcon icon={FiSend} size={14} /> Send
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailComposer;
